import React, {useEffect, useState} from 'react';
import AdminActionsService, {HallwayMonitorResponse} from 'src/services/AdminActionsService';
import Card from 'src/components/Cards/Card';
import TextInput from 'src/components/inputs/text-input';
import Label from 'shared/components/label';
import Button from 'src/components/button';
import {parseQueryString} from 'shared/utils';
import AreYouSure from 'src/components/AreYouSure';
import Loading from 'src/views/admin/Loading';
import {useBoolean} from 'src/hooks/useBoolean';
import Tooltip from 'src/components/tooltip';
import {setFromTargetValue} from '../../../utils/form';
import UserRepairModal from './user-repair-modal';

const promptForInformation = (question, callback): void => {
  const information = prompt(question, '');

  if (information) {
    callback(information);
  }
};

interface HallwayMonitorProps {
  flashMessage: (message: string) => void;
  featureFlags: {userDeletion: boolean};
  switchAccount: (userId: string, reason: string) => void;
}

const HallwayMonitor = (props: HallwayMonitorProps): React.ReactElement => {
  const loading = useBoolean(false);
  const noResults = useBoolean(false);
  const showRepairModal = useBoolean(false);
  const [hallwayMonitorResponse, setHallwayMonitorResponse] =
    useState<HallwayMonitorResponse | null>(null);
  const {q} = parseQueryString(window.location.search.slice(1));

  const [query, setQuery] = useState<string>(q || '');

  const fetchData = (searchQuery: string = query): void => {
    loading.setTrue();
    noResults.setFalse();

    if (query !== searchQuery) {
      setQuery(searchQuery);
    }

    setHallwayMonitorResponse(null);
    // ensures that the url gets updated so the url can be shared, or the page can be refreshed
    window.history.pushState(
      {},
      '',
      `/admin/tools/hallway-monitor?q=${encodeURIComponent(searchQuery)}`
    );
    AdminActionsService.hallwayMonitor(searchQuery)
      .then(setHallwayMonitorResponse)
      .then(loading.setFalse)
      .catch(() => {
        loading.setFalse();
        noResults.setTrue();
      });
  };

  useEffect((): void => {
    if (!query) {
      return;
    }

    fetchData();
  }, []);

  return (
    <div>
      <h1 className="shared-font headline">Hallway Monitor</h1>
      <Card>
        <Label>
          Find a user
          <div className="row">
            <div className="col-md-10">
              <TextInput
                onChange={setFromTargetValue(setQuery)}
                onKeyDown={(e): void => {
                  e.key === 'Enter' && fetchData();
                }}
                value={query}
                placeholder="Unique email, mobile number, database id, Raise Me code, or UGSS college student identifier"
              />
            </div>
            <div className="col-md-2">
              <Button onClick={(): void => fetchData()} margin={false}>
                Search
              </Button>
            </div>
          </div>
        </Label>
      </Card>
      {loading.value && <Loading />}
      {noResults.value && (
        <Card>
          <div className="alert alert-danger" role="alert">
            <strong>No user found</strong>
          </div>
        </Card>
      )}
      {hallwayMonitorResponse && (
        <>
          <Card className="clearfix">
            <h2 className="shared-font title">Actions</h2>
            {hallwayMonitorResponse.deleted && (
              <div className="alert alert-danger" role="alert">
                <strong>This user has been deleted, awaiting full deletion</strong>
              </div>
            )}
            {props.featureFlags.userDeletion && !hallwayMonitorResponse.deleted && (
              <div className="pull-left">
                <AreYouSure
                  id={`delete-${hallwayMonitorResponse.userId}`}
                  onYes={(): void => {
                    promptForInformation('Why are you deleting this user?', (reason) => {
                      AdminActionsService.deleteUser(hallwayMonitorResponse.userId, reason)
                        .then((): void => props.flashMessage('User Deleted!'))
                        .then((): void => fetchData(hallwayMonitorResponse.userId));
                    });
                  }}
                  buttonName="hallway-monitor delete user"
                  text="Are you sure you want to delete this user?"
                >
                  <Button
                    color="red"
                    buttonName="hallway-monitor are-you-sure delete user"
                    horizontalMargin
                  >
                    Delete User
                  </Button>
                </AreYouSure>
              </div>
            )}
            {hallwayMonitorResponse.deleted && (
              <div className="pull-left">
                <AreYouSure
                  id={`restore-${hallwayMonitorResponse.userId}`}
                  buttonText="Restore"
                  onYes={(): void => {
                    promptForInformation(
                      'What email address should be set for this user?',
                      (email) => {
                        AdminActionsService.restoreUser(hallwayMonitorResponse.userId, email)
                          .then((): void => props.flashMessage('User Restored!'))
                          .then((): void => fetchData());
                      }
                    );
                  }}
                  buttonName="hallway-monitor restore user"
                  text="Are you sure you want to restore this user?"
                >
                  <Button buttonName="hallway-monitor are-you-sure restore user" horizontalMargin>
                    Restore User
                  </Button>
                </AreYouSure>
              </div>
            )}
            {hallwayMonitorResponse.showAccountConversionButton && (
              <div className="pull-left">
                <AreYouSure
                  id={`unconvert-${hallwayMonitorResponse.userId}`}
                  buttonText="Unconvert"
                  onYes={(): void => {
                    AdminActionsService.revertConversion(hallwayMonitorResponse.userId)
                      .then((): void => props.flashMessage('User unconverted!'))
                      .then((): void => fetchData());
                  }}
                  buttonName="hallway-monitor restore user"
                  text="Are you sure you want to unconvert this user?"
                >
                  <Button
                    color="red"
                    buttonName="hallway-monitor are-you-sure unconvert user"
                    horizontalMargin
                  >
                    Undo Account Conversion
                  </Button>
                </AreYouSure>
              </div>
            )}
            {hallwayMonitorResponse.showVerifyEmailButton && (
              <Button
                buttonName="hallway-monitor verify email"
                className="pull-left"
                horizontalMargin
                onClick={(): void => {
                  AdminActionsService.verifyUserEmail(hallwayMonitorResponse.userId)
                    .then((): void => props.flashMessage('Email verified!'))
                    .then((): void => fetchData());
                }}
              >
                Verify Email
              </Button>
            )}
            {hallwayMonitorResponse.canImpersonateUser && !hallwayMonitorResponse.deleted && (
              <Button
                buttonName="hallway-monitor impersonate user"
                className="pull-left"
                onClick={(): void => {
                  promptForInformation('Why are you impersonating this user?', (reason) => {
                    localStorage.setItem('impersonationReturnUrl', window.location.href);
                    props.switchAccount(hallwayMonitorResponse.userId, reason);
                  });
                }}
                horizontalMargin
              >
                Impersonate User
              </Button>
            )}
            {hallwayMonitorResponse.userType === 'Student' && !hallwayMonitorResponse.deleted && (
              <Tooltip
                title="This will trigger a full evaluation in Pluto, it may still take a few minutes for the
              evaluation to be processed."
              >
                <div className="pull-left">
                  <Button
                    buttonName="hallway-monitor trigger evaluation"
                    horizontalMargin
                    onClick={(): void => {
                      AdminActionsService.triggerEvaluation(hallwayMonitorResponse.userId).then(
                        () => props.flashMessage('Evaluation Triggered!')
                      );
                    }}
                  >
                    Trigger Full Evaluation
                  </Button>
                </div>
              </Tooltip>
            )}
            {hallwayMonitorResponse.userType === 'Student' && !hallwayMonitorResponse.demo && (
              <div className="pull-left">
                <AreYouSure
                  id={`markdemo-${hallwayMonitorResponse.userId}`}
                  buttonText="Confirm"
                  onYes={(): void => {
                    AdminActionsService.updateDemoStatus(hallwayMonitorResponse.userId, true).then(
                      () => props.flashMessage('Marked as demo student!')
                    );
                  }}
                  buttonName="hallway-monitor mark as demo user"
                  text="Are you sure you want to mark this user as a demo user?"
                >
                  <Button
                    buttonName="hallway-monitor are-you-sure mark demo"
                    color="red"
                    horizontalMargin
                  >
                    Mark as demo
                  </Button>
                </AreYouSure>
              </div>
            )}
            {hallwayMonitorResponse.valid === false && !hallwayMonitorResponse.deleted && (
              <div className="pull-left">
                <Button
                  buttonName="hallway-monitor repair user"
                  horizontalMargin
                  onClick={showRepairModal.setTrue}
                >
                  Repair User
                </Button>
              </div>
            )}
          </Card>
          <Card>
            <h2 className="shared-font title">User attributes</h2>
            <div
              className={`alert alert-${hallwayMonitorResponse.valid ? 'success' : 'danger'}`}
              role="alert"
            >
              <strong>User {hallwayMonitorResponse.valid ? 'valid!' : 'Invalid :('}</strong>
              {hallwayMonitorResponse.invalidErrorMessages.map((message, index) => (
                <div key={index}>{message}</div>
              ))}
            </div>
            <pre>{hallwayMonitorResponse.attributes}</pre>
          </Card>
        </>
      )}
      {showRepairModal.value && hallwayMonitorResponse && (
        <UserRepairModal
          userId={hallwayMonitorResponse.userId}
          onClose={showRepairModal.setFalse}
          onRepaired={(): void => {
            showRepairModal.setFalse();
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default HallwayMonitor;
